import { logger } from '../logging/logger'

/**
 * What a haptic marks: `tap` confirms a keypress landing, `error` answers
 * refused input (deleting from empty, typing past the limit) with a heavier,
 * unmistakably-negative buzz.
 */
export type HapticKind = 'tap' | 'error'

/**
 * Resolved once, on the first tap: the peer's trigger, or `null` when it isn't
 * installed. Either way the answer is reused, so a host without it doesn't
 * retry a failing import on every keypress.
 */
let loading: Promise<((kind: HapticKind) => void) | null> | undefined

/**
 * Default haptics: lazily loads the optional `react-native-haptic-feedback`
 * peer and taps through it. A host without it taps silently; one that wants a
 * different module passes it to `FunkitProvider` as `hapticFeedback` instead —
 * which is what an Expo Go app does, since it can only link Expo's own modules.
 *
 * The import is dynamic and inside a `try`, which is what makes the peer
 * genuinely optional: Metro marks a dependency optional when its import can
 * reject, and skips it at resolution rather than failing the bundle. It also
 * keeps the native module unevaluated until a tap actually happens.
 */
export async function hapticTap(kind: HapticKind = 'tap'): Promise<void> {
  loading ??= loadTap()
  const trigger = await loading
  trigger?.(kind)
}

async function loadTap(): Promise<((kind: HapticKind) => void) | null> {
  try {
    const { trigger } = await import('react-native-haptic-feedback')
    // `impactLight`: the design taps to confirm a digit landed, not to mark an
    // event. Refused input is an event — `notificationError` is the platform's
    // exaggerated it-didn't-work buzz. Options left at their defaults — no
    // vibrate fallback on hardware without a taptic engine, and Android's
    // system haptics setting respected.
    return (kind) =>
      trigger(kind === 'error' ? 'notificationError' : 'impactLight')
  } catch (error) {
    // Not a failure — the peer is optional, and a silent keypad is a working
    // keypad. Said once, so a host that meant to have taps can find out why.
    logger.info(
      'connect-rn: taps are silent — install react-native-haptic-feedback, ' +
        'or pass FunkitProvider `hapticFeedback` to use your own haptics ' +
        'module (e.g. expo-haptics in Expo Go).',
      { error: error instanceof Error ? error.message : String(error) },
    )
    return null
  }
}
